import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  MenuItem,
  Alert,
  CircularProgress,
  Grid,
  Divider
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { Add as AddIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';

import questionService from '../../services/question.service';

// Validation schema
const QuestionSchema = Yup.object().shape({
  question_text: Yup.string().required('Question text is required'),
  option_a: Yup.string().required('Option A is required'),
  option_b: Yup.string().required('Option B is required'),
  option_c: Yup.string().required('Option C is required'),
  option_d: Yup.string().required('Option D is required'),
  correct_answer: Yup.string().required('Correct answer is required'),
  active_date: Yup.date().required('Active date is required'),
  difficulty: Yup.string().required('Difficulty level is required'),
  category: Yup.string().required('Category is required'),
  points: Yup.number().required('Points value is required').min(1, 'Points must be at least 1')
});

function QuestionCreate() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const initialValues = {
    question_text: '',
    option_a: '',
    option_b: '',
    option_c: '',
    option_d: '',
    correct_answer: 'A',
    active_date: new Date(),
    difficulty: 'medium',
    category: 'general',
    points: 10
  };

  const categories = [
    { value: 'general', label: 'General Knowledge' },
    { value: 'sports', label: 'Sports' },
    { value: 'entertainment', label: 'Entertainment' },
    { value: 'science', label: 'Science' },
    { value: 'history', label: 'History' },
    { value: 'geography', label: 'Geography' },
    { value: 'technology', label: 'Technology' },
    { value: 'current_affairs', label: 'Current Affairs' }
  ];

  const difficultLevels = [
    { value: 'easy', label: 'Easy' },
    { value: 'medium', label: 'Medium' },
    { value: 'hard', label: 'Hard' }
  ];

  const handleSubmit = async (values, { resetForm }) => {
    setLoading(true);
    setError(null);

    try {
      await questionService.createQuestion(values);
      navigate('/questions');
    } catch (err) {
      console.error('Failed to create question:', err);
      setError(err.message || 'Failed to create question. Please try again.');
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/questions')}
          sx={{ mb: 2 }}
        >
          Back to Questions
        </Button>
        <Typography variant="h4" component="h1" gutterBottom>
          Create Question
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Add a new trivia question
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
        <CardContent>
          <Formik
            initialValues={initialValues}
            validationSchema={QuestionSchema}
            onSubmit={handleSubmit}
          >
            {({ errors, touched, values, setFieldValue }) => (
              <Form>
                <Grid container spacing={4}>
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Question Details
                    </Typography>
                    <Divider sx={{ mb: 3 }} />
                    
                    <Field
                      as={TextField}
                      name="question_text"
                      label="Question Text"
                      fullWidth
                      multiline
                      rows={3}
                      margin="normal"
                      variant="outlined"
                      error={touched.question_text && Boolean(errors.question_text)}
                      helperText={touched.question_text && errors.question_text}
                    />
                    
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid item xs={12} sm={6}>
                        <Field
                          as={TextField}
                          name="category"
                          label="Category"
                          select
                          fullWidth
                          margin="normal"
                          variant="outlined"
                          error={touched.category && Boolean(errors.category)}
                          helperText={touched.category && errors.category}
                        >
                          {categories.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Field>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Field
                          as={TextField}
                          name="difficulty"
                          label="Difficulty"
                          select
                          fullWidth
                          margin="normal"
                          variant="outlined"
                          error={touched.difficulty && Boolean(errors.difficulty)}
                          helperText={touched.difficulty && errors.difficulty}
                        >
                          {difficultLevels.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Field>
                      </Grid>
                    </Grid>
                    
                    <Field
                      as={TextField}
                      name="points"
                      label="Points Value"
                      type="number"
                      fullWidth
                      margin="normal"
                      variant="outlined"
                      error={touched.points && Boolean(errors.points)}
                      helperText={touched.points && errors.points}
                    />
                    
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        label="Active Date"
                        value={values.active_date}
                        onChange={(newValue) => {
                          setFieldValue('active_date', newValue);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            margin="normal"
                            error={touched.active_date && Boolean(errors.active_date)}
                            helperText={touched.active_date && errors.active_date}
                          />
                        )}
                      />
                    </LocalizationProvider>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Answer Options
                    </Typography>
                    <Divider sx={{ mb: 3 }} />
                    
                    <Box sx={{ mb: 2 }}>
                      <FormControl component="fieldset" sx={{ mb: 2 }}>
                        <FormLabel component="legend">Correct Answer</FormLabel>
                        <RadioGroup
                          row
                          name="correct_answer"
                          value={values.correct_answer}
                          onChange={(e) => setFieldValue('correct_answer', e.target.value)}
                        >
                          <FormControlLabel value="A" control={<Radio />} label="A" />
                          <FormControlLabel value="B" control={<Radio />} label="B" />
                          <FormControlLabel value="C" control={<Radio />} label="C" />
                          <FormControlLabel value="D" control={<Radio />} label="D" />
                        </RadioGroup>
                      </FormControl>
                    </Box>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Field
                          as={TextField}
                          name="option_a"
                          label="Option A"
                          fullWidth
                          margin="normal"
                          variant="outlined"
                          error={touched.option_a && Boolean(errors.option_a)}
                          helperText={touched.option_a && errors.option_a}
                          InputProps={{
                            startAdornment: (
                              <Box
                                sx={{
                                  bgcolor: values.correct_answer === 'A' ? 'primary.main' : 'grey.300',
                                  color: values.correct_answer === 'A' ? 'white' : 'text.primary',
                                  width: 24,
                                  height: 24,
                                  borderRadius: '50%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  mr: 1,
                                  fontWeight: 'bold',
                                  fontSize: '0.85rem'
                                }}
                              >
                                A
                              </Box>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Field
                          as={TextField}
                          name="option_b"
                          label="Option B"
                          fullWidth
                          margin="normal"
                          variant="outlined"
                          error={touched.option_b && Boolean(errors.option_b)}
                          helperText={touched.option_b && errors.option_b}
                          InputProps={{
                            startAdornment: (
                              <Box
                                sx={{
                                  bgcolor: values.correct_answer === 'B' ? 'primary.main' : 'grey.300',
                                  color: values.correct_answer === 'B' ? 'white' : 'text.primary',
                                  width: 24,
                                  height: 24,
                                  borderRadius: '50%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  mr: 1,
                                  fontWeight: 'bold',
                                  fontSize: '0.85rem'
                                }}
                              >
                                B
                              </Box>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Field
                          as={TextField}
                          name="option_c"
                          label="Option C"
                          fullWidth
                          margin="normal"
                          variant="outlined"
                          error={touched.option_c && Boolean(errors.option_c)}
                          helperText={touched.option_c && errors.option_c}
                          InputProps={{
                            startAdornment: (
                              <Box
                                sx={{
                                  bgcolor: values.correct_answer === 'C' ? 'primary.main' : 'grey.300',
                                  color: values.correct_answer === 'C' ? 'white' : 'text.primary',
                                  width: 24,
                                  height: 24,
                                  borderRadius: '50%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  mr: 1,
                                  fontWeight: 'bold',
                                  fontSize: '0.85rem'
                                }}
                              >
                                C
                              </Box>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Field
                          as={TextField}
                          name="option_d"
                          label="Option D"
                          fullWidth
                          margin="normal"
                          variant="outlined"
                          error={touched.option_d && Boolean(errors.option_d)}
                          helperText={touched.option_d && errors.option_d}
                          InputProps={{
                            startAdornment: (
                              <Box
                                sx={{
                                  bgcolor: values.correct_answer === 'D' ? 'primary.main' : 'grey.300',
                                  color: values.correct_answer === 'D' ? 'white' : 'text.primary',
                                  width: 24,
                                  height: 24,
                                  borderRadius: '50%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  mr: 1,
                                  fontWeight: 'bold',
                                  fontSize: '0.85rem'
                                }}
                              >
                                D
                              </Box>
                            ),
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
                
                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/questions')}
                    sx={{ mr: 2 }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    startIcon={loading ? <CircularProgress size={24} color="inherit" /> : <AddIcon />}
                    disabled={loading}
                  >
                    {loading ? 'Creating...' : 'Create Question'}
                  </Button>
                </Box>
              </Form>
            )}
          </Formik>
        </CardContent>
      </Card>
    </Container>
  );
}

export default QuestionCreate;